import * as cosmwasm from '@cosmjs/cosmwasm-stargate';
import { network } from 'config/networks';
import { Decimal } from '@cosmjs/math';
import { isDeliverTxFailure, logs, GasPrice, Coin } from '@cosmjs/stargate';
import { toUtf8 } from '@cosmjs/encoding';
import { MsgExecuteContract } from 'cosmjs-types/cosmwasm/wasm/v1/tx';
import { networkInfo } from 'config/constants';
import { OfflineSigner } from '@cosmjs/proto-signing';
import { Tendermint37Client } from '@cosmjs/tendermint-rpc';
import { MetamaskOfflineSigner } from './eip191';
import { getWalletByNetworkCosmosFromStorage } from './utils';

/**
 * The options of an .instantiate() call.
 * All properties are optional.
 */
export interface HandleOptions {
  readonly memo?: string;
  readonly funds?: readonly Coin[];
}

export interface ExecuteMultipleMsg {
  contractAddress: string;
  handleMsg: string;
  handleOptions?: HandleOptions;
}

interface Msg {
  contractAddress: string;
  handleMsg: any;
  transferAmount?: readonly Coin[];
}

const collectWallet = async (chainId: string) => {
  const keplr = await window.Keplr.getKeplr();
  if (keplr) return await keplr.getOfflineSignerAuto(chainId);
  if (window.ethereum) return await MetamaskOfflineSigner.connect(window.ethereum, network.denom);
  throw new Error('You have to install Cosmos wallet first if you do not use a mnemonic to sign transactions');
};

const parseExecuteContractMultiple = (msgs: ExecuteMultipleMsg[]) => {
  console.log('messages in parse execute contract: ', msgs);
  return msgs.map(({ handleMsg, handleOptions, contractAddress }) => {
    return {
      handleMsg: JSON.parse(handleMsg),
      transferAmount: handleOptions?.funds,
      contractAddress
    };
  });
};

const getExecuteContractMsgs = (senderAddress: string, msgs: Msg[]) => {
  return msgs.map(({ handleMsg, transferAmount, contractAddress }) => {
    return {
      typeUrl: '/cosmwasm.wasm.v1.MsgExecuteContract',
      value: MsgExecuteContract.fromPartial({
        sender: senderAddress,
        contract: contractAddress,
        msg: toUtf8(JSON.stringify(handleMsg)),
        funds: [...(transferAmount || [])]
      })
    };
  });
};

const getAminoExecuteContractMsgs = (senderAddress: string, msgs: Msg[]) => {
  return msgs.map(({ handleMsg, transferAmount, contractAddress }) => {
    return {
      type: 'wasm/MsgExecuteContract',
      value: {
        sender: senderAddress,
        contract: contractAddress,
        msg: handleMsg,
        sent_funds: [...(transferAmount || [])]
      }
    };
  });
};

const executeMultipleDirectClient = async (
  senderAddress: string,
  msgs: Msg[],
  memo = '',
  client: cosmwasm.SigningCosmWasmClient
) => {
  const executeContractMsgs = getExecuteContractMsgs(senderAddress, msgs);

  const result = await client.signAndBroadcast(senderAddress, executeContractMsgs, 'auto', memo);
  if (isDeliverTxFailure(result)) {
    throw new Error(
      `Error when broadcasting tx ${result.transactionHash} at height ${result.height}. Raw log: ${result.rawLog}`
    );
  }
  return {
    logs: logs.parseRawLog(result.rawLog),
    transactionHash: result.transactionHash
  };
};

const executeMultipleAminoClient = async (
  msgs: Msg[],
  memo = '',
  client: cosmwasm.SigningCosmWasmClient,
  walletAddr: string
) => {
  const executeMsgs = getExecuteContractMsgs(walletAddr, msgs);

  const result = await client.signAndBroadcast(walletAddr, executeMsgs, 'auto', memo);
  if (isDeliverTxFailure(result)) {
    throw new Error(
      `Error when broadcasting tx ${result.transactionHash} at height ${result.height}. Code: ${result.code}; Raw log: ${result.rawLog}`
    );
  }
  return {
    logs: result?.rawLog,
    transactionHash: result.transactionHash
  };
};
class CosmJs {
  static getWalletByFromStorage() {
    return getWalletByNetworkCosmosFromStorage();
  }

  static async execute(data: {
    prefix?: string;
    walletAddr: string;
    address: string;
    handleMsg: string;
    handleOptions?: HandleOptions;
    gasAmount: Coin;
    gasLimits?: { exec: number };
    memo?: string;
  }) {
    try {
      const walletType = this.getWalletByFromStorage();
      const keplr = await window.Keplr.getKeplr();
      if (keplr || (walletType && walletType === 'eip191')) {
        // await window.Keplr.suggestChain(networkInfo);
        const key = await window.Keplr.getKeplrKey(network.chainId);
        if (key?.isNanoLedger || (walletType && walletType === 'eip191')) return this.executeAmino(data);
        return this.executeDirect(data);
      } else {
        throw new Error('You need to install Keplr to execute the contract');
      }
    } catch (error) {
      console.log('error in executing contract: ' + error);
      throw error;
    }
  }

  static async executeMultiple(data: {
    prefix?: string;
    walletAddr: string;
    msgs: ExecuteMultipleMsg[];
    gasAmount: Coin;
    gasLimits?: { exec: number };
    memo?: string;
  }) {
    try {
      const walletType = this.getWalletByFromStorage();
      const keplr = await window.Keplr.getKeplr();
      const isEip191 = walletType && walletType === 'eip191';
      if (keplr || isEip191) {
        await window.Keplr.suggestChain('Oraichain');
        const key = await window.Keplr.getKeplrKey(network.chainId);
        if (key?.isNanoLedger || isEip191) return this.executeMultipleAmino(data);
        return this.executeMultipleDirect(data);
      } else {
        throw new Error('You need to install Keplr to execute the contract');
      }
    } catch (error) {
      console.log('error in executing contract: ' + error);
      throw error;
    }
  }

  static async sendMultipleAmino({
    msgs,
    gasAmount,
    walletAddr,
    lcd = network.rpc
  }: {
    walletAddr: string;
    msgs: any[];
    gasAmount: Coin;
    lcd?: string;
    chainId?: string;
  }) {
    const result = await window.client.signAndBroadcast(walletAddr, msgs, 'auto');
    if (isDeliverTxFailure(result)) {
      throw new Error(
        `Error when broadcasting tx ${result.transactionHash} at height ${result.height}. Code: ${result.code}; Raw log: ${result.rawLog}`
      );
    }
    return {
      logs: result.rawLog,
      transactionHash: result.transactionHash
    };
  }

  static async executeAmino({
    address,
    handleMsg,
    handleOptions,
    gasAmount,
    prefix,
    walletAddr
  }: {
    prefix?: string;
    walletAddr: string;
    address: string;
    handleMsg: string;
    handleOptions?: HandleOptions;
    gasAmount: Coin;
  }) {
    try {
      const input = JSON.parse(handleMsg);

      const result = await window.client.execute(walletAddr, address, input, 'auto', undefined, handleOptions?.funds);

      return result;
    } catch (error) {
      console.log('error in executing contract: ' + error);
      throw error;
    }
  }

  static async executeMultipleAmino({
    msgs,
    gasAmount,
    prefix,
    walletAddr
  }: {
    prefix?: string;
    walletAddr: string;
    msgs: ExecuteMultipleMsg[];
    gasAmount: Coin;
  }) {
    try {
      const input: Msg[] = msgs.map(({ handleMsg, handleOptions, contractAddress }) => {
        return {
          handleMsg: JSON.parse(handleMsg),
          transferAmount: handleOptions?.funds,
          contractAddress
        };
      });

      const result = await executeMultipleAminoClient(input, '', window.client, walletAddr);

      return result;
    } catch (error) {
      console.log('error in executing contract: ' + error);
      throw error;
    }
  }

  /**
   * A wrapper method to execute a smart contract
   * @param args - an object containing essential parameters to execute contract
   * @returns - transaction hash after executing the contract
   */
  static async executeDirect({
    address,
    handleMsg,
    handleOptions,
    gasAmount,
    prefix,
    walletAddr
  }: {
    prefix?: string;
    walletAddr: string;
    address: string;
    handleMsg: string;
    handleOptions?: HandleOptions;
    gasAmount: Coin;
  }) {
    try {
      const input = JSON.parse(handleMsg);
      const result = await window.client.execute(walletAddr, address, input, 'auto', '', handleOptions?.funds);
      return result;
    } catch (error) {
      console.log('error in executing contract: ', error);
      throw error;
    }
  }

  static async executeMultipleDirect({
    msgs,
    gasAmount,
    prefix,
    walletAddr
  }: {
    prefix?: string;
    walletAddr: string;
    msgs: ExecuteMultipleMsg[];
    gasAmount: Coin;
  }) {
    try {
      const input: Msg[] = msgs.map(({ handleMsg, handleOptions, contractAddress }) => {
        return {
          handleMsg: JSON.parse(handleMsg),
          transferAmount: handleOptions?.funds,
          contractAddress
        };
      });
      const result = await executeMultipleDirectClient(walletAddr, input, undefined, window.client);
      return result;
    } catch (error) {
      console.log('error in executing contract: ', error);
      throw error;
    }
  }
}

export { collectWallet, getExecuteContractMsgs, parseExecuteContractMultiple, getAminoExecuteContractMsgs };

export default CosmJs;
